export const READING_TIPS_STORAGE_KEY = 'liwaza.gre.readingTipsOpen';

export const READING_TIPS_GROUPS = [
  {
    id: 'reading-the-passage',
    title: 'Reading the passage',
    tips: [
      {
        id: 'rc-map-passage',
        title: 'Map the passage, not the details',
        rule: 'Your first read should produce a map: what the author is doing, where the evidence lives, and where the view shifts. You do not need every clause on the first pass. Dense jargon and secondary examples can wait until a question forces you there.',
        howTo: [
          'End each paragraph with a one-line label such as claim, evidence, objection, or reply.',
          'Keep a running summary of about 12 words per paragraph, in your own words.',
          'If you feel lost, reset by asking: “What single claim would the author defend in a debate?”',
        ],
        example:
          'A useful map looks like “old view → new evidence → author prefers the new view.” That chain answers most passage-level questions without memorizing scientific details.',
        examTrap:
          'If you cannot state the passage’s arc in two sentences, pause before answering primary purpose questions.',
      },
      {
        id: 'rc-first-read',
        title: 'Spend your time on the first read',
        rule: 'A slightly slower first read that captures structure often cuts total time. Cheap rereads are expensive: if you rush the passage, you reread everything. Hunt for the author’s bottom line, tone markers, and where the hardest proof lives.',
        howTo: [
          'Halfway through, check that you can state the author’s leaning.',
          'Answer global questions before burning time on a single detail question.',
          'If you must reread, reread one paragraph, not the whole passage.',
        ],
        example:
          'On a longer passage, spend about 3 to 5 minutes reading with focus, then aim to answer each question in 30 to 45 seconds because the logic is already clear.',
        examTrap:
          'If you are rereading the opening paragraph for every question, your first read probably missed the pivot structure.',
      },
      {
        id: 'rc-pivots-concessions',
        title: 'Pivots and concessions carry the real argument',
        rule: 'Words like however, although, and in fact redirect the argument. Authors often concede a limited point so the reader trusts them when they pivot. The clause after the pivot is usually what they want you to walk away believing.',
        howTo: [
          'Bracket the clause after a pivot and ask: “Is this the author’s real view?”',
          'Underline the “but” that follows a concession.',
          'Do not let the pre-pivot view sneak into a main idea answer.',
        ],
        example:
          'In “While the theory explains some data, it fails to account for later findings,” the main point is the failure, not the limited success.',
        examTrap:
          'Main idea wrong answers often recycle the pre-pivot content because it appears early and feels familiar.',
      },
    ],
  },
  {
    id: 'question-types',
    title: 'Question types',
    tips: [
      {
        id: 'rc-main-purpose-tone',
        title: 'Main idea, purpose, and tone work together',
        rule: 'Main idea is what the passage is centrally about. Purpose is why the author wrote it. Tone is the author’s attitude toward the material. When these three agree, wrong answers become obvious. A dramatic topic can still have a measured, analytical voice.',
        howTo: [
          'After the passage, finish: “The author mainly wants to …”',
          'Write purpose as an infinitive: “to show that…”, “to challenge…”',
          'Pick one tone adjective and check that it fits the author’s verbs and qualifiers.',
        ],
        example:
          'Main idea = “sleep affects memory”; purpose = “to challenge researchers who downplay sleep”; tone = “measured but corrective.”',
        examTrap:
          'If two tone answers differ mainly in intensity, pick the milder one unless the passage escalates. GRE authors rarely rant.',
      },
      {
        id: 'rc-according-infer',
        title: '“According to” vs “It can be inferred”',
        rule: '“According to the passage” is a contract: the answer must be directly supported by explicit language or an unavoidable paraphrase. “It can be inferred” allows one controlled step, never a leap into outside knowledge or a stronger claim than the author made.',
        howTo: [
          'Underline the exact phrase that supports an “according to” answer.',
          'For inference, ask: “Would the author agree without adding new facts?”',
          'Reject answers that are stronger than the passage: always, never, proves, eliminates.',
        ],
        example:
          'If the passage says “The policy reduced emissions in several coastal cities,” then “the policy lowered emissions in some cities” is supported. “The policy will solve climate change nationwide” is not a valid inference.',
        examTrap:
          'Extreme or universal claims are almost never valid inferences unless the passage uses equally strong language.',
      },
      {
        id: 'rc-function-role',
        title: 'Function questions ask about role, not summary',
        rule: 'These questions ask why a sentence exists in the argument’s architecture: background, counterexample, concession, evidence, or transition. The right answer describes that job, not the sentence’s topic.',
        howTo: [
          'Name the paragraph’s local claim before judging the sentence’s role.',
          'Prefer answers about structure (“supports,” “qualifies,” “introduces a challenge”).',
          'Remove the sentence mentally: if the argument jumps, it was doing real work.',
        ],
        example:
          'If a paragraph introduces a theory and the next sentence gives a historical case, that sentence likely functions as supporting evidence, not as the main point itself.',
        examTrap:
          'Beware choices that sound like the main idea. Examples rarely are the main idea.',
      },
      {
        id: 'rc-strengthen-weaken',
        title: 'Strengthen and weaken the exact claim',
        rule: 'Focus on the specific claim being argued, not the general topic. A choice matters only if it changes the credibility of that precise claim. Irrelevant facts about the same subject are still wrong.',
        howTo: [
          'Restate the exact claim in one clause before looking at the answers.',
          'Ask whether the choice makes that claim more or less believable.',
          'Drop answers that change the subject, even if they sound GRE-like.',
        ],
        example:
          'If the passage argues that a farming method reduces soil erosion, an answer about crop taste is irrelevant. An answer about measured erosion rates directly affects the claim.',
        examTrap:
          'If you would need one extra sentence in the passage to make an answer airtight, it is probably wrong.',
      },
      {
        id: 'rc-vocab-context',
        title: 'Use context for difficult vocabulary',
        rule: 'You rarely need the dictionary definition. You need polarity and function: whether the word is positive, negative, neutral, or a contrast. Surrounding logic, parallel structure, and “not X but Y” patterns usually give you enough.',
        howTo: [
          'Replace the unknown word with a neutral placeholder and reread the sentence.',
          'Look at what the word is parallel to in a list or comparison.',
          'If two remaining choices are opposites, re-check nearby contrast words.',
        ],
        example:
          'If a theory is described as “initially celebrated but later discredited,” an unfamiliar adjective nearby can still be read as praise or criticism from that structure alone.',
        examTrap:
          'When vocabulary is tough, answer the logic first. Often only one choice fits the structural role.',
      },
    ],
  },
  {
    id: 'traps-and-elimination',
    title: 'Traps and elimination',
    tips: [
      {
        id: 'rc-recycled-words',
        title: 'Match meaning, not recycled words',
        rule: 'Wrong answers reuse shiny words from the passage. Correct answers often paraphrase the logic with different vocabulary. The hardest traps are 90% correct until one word shifts scope, certainty, or who holds the view.',
        howTo: [
          'Cover the choices briefly and predict the shape of the right answer.',
          'Circle quantifiers and modals in both the passage and the answers.',
          'When a choice repeats three words from the passage, slow down and test the logic.',
        ],
        example:
          'Compare “some researchers” with “all researchers,” or “may help” with “definitely solves.” That single change often reveals the trap.',
        examTrap:
          'If a choice feels too easy because it echoes the passage verbatim, treat that as a warning. If the passage hedges, the correct answer usually hedges too.',
      },
      {
        id: 'rc-eliminate-skip',
        title: 'Eliminate aggressively, then skip and return',
        rule: 'You win RC by becoming fast at spotting fatal flaws: out of scope, reversed causation, stronger certainty than the passage, wrong actor, or inconsistent tone. You do not need perfect certainty to start eliminating. If a question stalls, move on.',
        howTo: [
          'Ask of each choice: “Did anyone in the passage actually say or imply this?”',
          'If you can kill two choices in thirty seconds, spend the remaining time on the harder pair.',
          'If you are still between two choices after about 45 seconds, mark it and come back.',
        ],
        example:
          'If the author cautiously suggests something “may” be true, eliminate any answer that says it “proves” or “guarantees” the claim, then move on if the last two still feel close.',
        examTrap:
          'When stuck between two answers, argue against each one. The one that survives a gentle attack is usually correct.',
      },
    ],
  },
];

export const READING_TIPS_COUNT = READING_TIPS_GROUPS.reduce(
  (count, group) => count + group.tips.length,
  0
);
